import { Currency, CurrencyAmount, TradeType } from '@kodiak-finance/sdk-core'
import { SupportedChainId } from 'config/chains'
import { DEFAULT_AUTO_SLIPPAGE } from 'hooks/useAutoSlippageTolerance'
import { useCallback, useMemo } from 'react'
import { useUserSlippageToleranceWithDefault } from 'state/user/hooks'
import { zeroAddress } from 'viem'

/**
 * Returns query arguments for the Routing API query or undefined if the
 * query should be skipped. Input arguments do not need to be memoized, as they will
 * be destructured.
 */
export function useRoutingAPIArguments({
  tokenIn,
  tokenOut,
  amount,
  tradeType,
  providers,
  recipient,
}: {
  tokenIn: Currency | undefined
  tokenOut: Currency | undefined
  amount: CurrencyAmount<Currency> | undefined
  tradeType: TradeType
  providers: string[]
  recipient?: string | null
}) {
  const allowedSlippage = useUserSlippageToleranceWithDefault(DEFAULT_AUTO_SLIPPAGE)

  const getTokenAddress = useCallback((token: Currency) => {
    return token.isNative ? zeroAddress : token.wrapped.address
  }, [])

  return useMemo(() => {
    if (!tokenIn || !tokenOut || !amount) return undefined
    if (tokenIn.equals(tokenOut)) {
      throw new Error('tokenIn and tokenOut cannot be the same')
    }
    const tokenInChainId = tokenIn.wrapped.chainId as SupportedChainId
    const tokenOutChainId = tokenOut.wrapped.chainId as SupportedChainId
    if (tokenInChainId !== tokenOutChainId) {
      throw new Error('tokenInChainId and tokenOutChainId must be identical')
    }

    return {
      amount: amount.quotient.toString(),
      tokenInAddress: getTokenAddress(tokenIn),
      tokenInChainId,
      tokenOutAddress: getTokenAddress(tokenOut),
      tokenOutChainId,
      type: (tradeType === TradeType.EXACT_INPUT ? 'exactIn' : 'exactOut') as 'exactIn' | 'exactOut',
      recipient: recipient ?? undefined,
      slippageTolerance: parseFloat(allowedSlippage.toFixed(2)),
      refCode: 1,
      providers: providers.join(','),
    }
  }, [tokenIn, tokenOut, amount, tradeType, recipient, providers, allowedSlippage, getTokenAddress])
}
